import AxiosInstance from '@/api/axios';
import { toast } from '@/components/Base/Toast/ToastManager';
import { getClientId } from '@/utils/helper';
import { FileResponseType } from '@/utils/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { replaceFileByIntent } from './trainingFilesSlice';

interface ITrainingVersionParams {
  file: string;
  branch?: string | null;
  region?: string | null;
  page?: number;
  perpage?: number;
}
export const fetchTrainingVersion = createAsyncThunk(
  'fetchTrainingVersions/fetchData',
  async (
    { file, branch, region, page = 1, perpage = 10 }: ITrainingVersionParams,
    thunkAPI
  ) => {
    try {
      console.log('started fetching');
      const client = getClientId();
      const response = await AxiosInstance.get(
        `/files/${file}/versions?client=${client}&branch=${branch}&region=${region}&page=${page}&perpage=${perpage}`
      );
      const {
        versions,
        total,
        totalPages,
        page: returnedPage,
        perpage: returnedPerpage,
      } = response.data.versions;

      return {
        versions,
        total,
        totalPages,
        page: returnedPage,
        perpage: returnedPerpage,
      };
    } catch (error) {
      console.log(error);
    }
  }
);

export type FileHistoryResponse = FileResponseType & {
  email: string;
  timezone: string;
};

interface ITrainingVersions {
  loading: boolean;
  loadingMore: boolean;
  error: unknown | null;
  versions: FileHistoryResponse[];
  total: number;
  totalPages: number;
  currentPage: number;
}

const initialState: ITrainingVersions = {
  loading: false,
  loadingMore: false,
  error: null,
  versions: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
};

export const replaceFile = createAsyncThunk(
  'files/replaceFile',
  async ({ file, branch, region }: ITrainingVersionParams, thunkAPI) => {
    try {
      const client = getClientId();
      const response = await AxiosInstance.put(
        `/files/${file}/revert?client=${client}&branch=${branch}&region=${region}`
      );
      thunkAPI.dispatch(replaceFileByIntent(response.data.data));
      toast.show({
        type: 'primary',
        title: 'File reverted successfully',
        content: 'Changes saved successfully.',
        duration: 2000,
      });
      return response.data.data;
    } catch (error: any) {
      toast.show({
        type: 'warning',
        title: 'File could not be reverted.',
        content: 'Changes not saved.',
        duration: 2000,
      });
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const trainingVersionReducer = createSlice({
  name: 'trainingVersion',
  initialState,
  reducers: {
    updateVersions: (state, action: PayloadAction<FileHistoryResponse[]>) => {
      state.versions = action.payload;
    },
    resetVersions: (state) => {
      state.versions = [];
      state.currentPage = 1;
      state.total = 0;
      state.totalPages = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrainingVersion.pending, (state, action) => {
        const isFirstPage = (action.meta.arg.page ?? 1) === 1;
        if (isFirstPage) {
          state.loading = true;
        } else {
          state.loadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchTrainingVersion.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        if (!action.payload) return;

        const { versions, total, totalPages, page } = action.payload;

        if (page === 1) {
          state.versions = versions;
        } else {
          state.versions = [...state.versions, ...versions];
        }

        state.total = total;
        state.totalPages = totalPages;
        state.currentPage = page;
      })
      .addCase(fetchTrainingVersion.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload as string;
      })

      .addCase(replaceFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replaceFile.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload) return;
        const updatedFile = action.payload;
        state.versions = state.versions.map((version) => ({
          ...version,
          replaced: true,
        }));
        state.total = state.total + 1;
        state.versions.unshift({ ...updatedFile, replaced: false });
      })
      .addCase(replaceFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { updateVersions, resetVersions } = trainingVersionReducer.actions;
export default trainingVersionReducer.reducer;
